import axios, {AxiosResponse} from 'axios'
import clsx from 'clsx'
import React, {Dispatch, FC, ReactNode, SetStateAction, useEffect, useState} from 'react'
import {FormatOptionLabelMeta, StylesConfig} from 'react-select'
import AsyncSelect from 'react-select/async'
import {InputHint} from './InputHint'
import {API_URL, isNotEmpty} from '../crud-helper/helpers'
import {FieldHelpLabel} from './FieldHelp'
import {
  BusinessOption,
  BusinessOptionData,
  businessOptionStyle,
} from '../../../app/modules/apps/shared/business-switcher/BusinessOption'

export type SearchSelectOption = {
  value: any
  label: ReactNode
  disabled?: boolean
  /** The business's parts, sent by the «adminCompany» search (backend 1.0.0.638+): shown on two lines. */
  business?: BusinessOptionData
  /** What typing in the box searches, for an option whose label is markup rather than text. */
  searchText?: string
}

const optionText = (option: SearchSelectOption) => option.searchText ?? option.label?.toString() ?? ''

const formatOptionLabel = (
  option: SearchSelectOption,
  {context}: FormatOptionLabelMeta<SearchSelectOption>
) =>
  option.business ? (
    <BusinessOption business={option.business} compact={context === 'value'} showId={context === 'menu'} />
  ) : (
    option.label
  )

const selectStyles: StylesConfig<SearchSelectOption, boolean> = {
  menuPortal: (base) => ({...base, zIndex: 10000}),
  menu: (base) => ({...base, zIndex: 10000}),
  option: (base, state) => (state.data.business ? businessOptionStyle(base, state) : base),
}

type props = {
  section: string
  stateValue: {
    value: SearchSelectOption | undefined
    setValue: Dispatch<SetStateAction<SearchSelectOption | undefined>>
  }
  placeholder?: string
  label?: ReactNode
  isRequired?: boolean
  children?: React.ReactNode
  error?: string
  hint?: ReactNode
  defaultvalue?: number
  isDisabled?: boolean
  items?: SearchSelectOption[]
  isMulti?: boolean
  extraData?: object
  helpKey?: string
}

const SearchSelect: FC<props> = ({
  section,
  placeholder,
  label,
  isRequired,
  stateValue,
  children,
  hint,
  error,
  defaultvalue,
  items,
  isDisabled = false,
  isMulti = false,
  extraData = null,
  helpKey,
}) => {
  const [loaded, setLoaded] = useState(defaultvalue == undefined)
  const [value, setValue] = useState<SearchSelectOption | SearchSelectOption[]>()
  const staticItems = items || []
  const extraDataCacheKey = extraData ? JSON.stringify(extraData) : ''
  const staticItemsCacheKey = items
    ? staticItems.map((item) => `${item.value}:${optionText(item)}`).join('|')
    : `${section}:${extraDataCacheKey}`
  const handleChange = (selectedOption: any) => {
    stateValue.setValue(selectedOption)
  }
  function loadData(search: string, calback: (options: SearchSelectOption[]) => void) {
    if (items) {
      let _items = staticItems
      if (isNotEmpty(search)) {
        _items = staticItems.filter((x) => x.label && x.label != null && optionText(x).includes(search))
      }
      calback(_items)
      if (!loaded && defaultvalue != undefined) {
        const defaultItem = staticItems.find((x) => x.value == defaultvalue)
        if (defaultItem) {
          stateValue.setValue(defaultItem)
        }
        setLoaded(true)
      }
    } else {
      axios
        .post(`${API_URL}/searchselect/`, {
          section: section,
          search: search,
          defaultvalue: loaded ? undefined : defaultvalue,
          extraData: extraData,
        })
        .then((d: AxiosResponse<SearchSelectOption[]>) => {
          if (!loaded) {
            stateValue.setValue(d.data.find((x) => x.value == defaultvalue))
            setLoaded(true)
          }
          calback(d.data)
        })
    }
  }
  const loadingMessage = () => {
    return <>درحال جستجو...</>
  }
  const noOptionsMessage = () => {
    return <>موردی یافت نشد</>
  }

  const menuPortalTarget = typeof document !== 'undefined' ? document.body : undefined

  useEffect(() => {
    setValue(stateValue.value)
  }, [stateValue.value])

  useEffect(() => {
    if (items && defaultvalue != undefined && !stateValue.value) {
      const defaultItem = staticItems.find((x) => x.value == defaultvalue)
      if (defaultItem) {
        stateValue.setValue(defaultItem)
        setLoaded(true)
      }
    }
  }, [staticItemsCacheKey])

  return (
    <>
      {label && (
        <>
          <label className={clsx('fw-bold fs-6 mb-2', {required: isRequired})}>
            <FieldHelpLabel label={label} helpKey={helpKey} isRequired={isRequired} />
          </label>
        </>
      )}
      <div className='d-flex flex-row align-items-center gap-2 mt-3'>
        <div className='flex-grow-1'>
          <AsyncSelect
            key={`${section}-${staticItemsCacheKey}`}
            isDisabled={isDisabled}
            placeholder={placeholder || 'جستجو کنید...'}
            noOptionsMessage={noOptionsMessage}
            loadingMessage={loadingMessage}
            loadOptions={loadData}
            defaultOptions={items ? staticItems : true}
            openMenuOnClick
            isClearable
            cacheOptions={staticItemsCacheKey}
            menuPosition='fixed'
            menuPortalTarget={menuPortalTarget}
            styles={selectStyles}
            formatOptionLabel={formatOptionLabel}
            onChange={handleChange}
            value={value}
            isMulti={isMulti}
          />
        </div>
        <div>{children}</div>
      </div>
      <InputHint hint={hint} error={error} />
    </>
  )
}

export {SearchSelect}
